import React, { useMemo } from 'react';
import BreakdownTable, {
  type BreakdownCell,
  type BreakdownCellContext,
} from '@/components/ui/breakdown-tables/BreakdownTable';
import { SupplierPayment } from '@/api/rebates/types';
import {
  RebatePoint,
  REBATE_SUPPLIER_PAYMENT_TYPE_LABELS,
  RebateSupplierPaymentType,
} from '@erp/shared-types';
import { formatPaymentDate } from 'src/utils/dateUtils';
import { formatCurrency } from '@erp/shared-utils';
import { deriveCumulativePosition } from 'src/utils/rebateUtils';
import { Badge } from '@/components/ui/badge';
import { CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { aggregateDebtorsReportCellValues } from '@/utils/rebates/debtorsReportAggregation';
import { DataSourceType } from '@/components/common/DataSourceSelect';

interface DebtorsReportTableProps {
  suppliers: { id: string; name: string }[];
  paymentsData: SupplierPayment[];
  rebateData: RebatePoint[];
  showClosedAgreements?: boolean;
  dataSource?: DataSourceType;
}

type PaymentStatus =
  | 'matched'
  | 'slightly_underpaid'
  | 'underpaid'
  | 'slightly_overpaid'
  | 'overpaid'
  | 'not_paid'
  | 'closed';

function calculatePaymentStatus(
  forecasted: number,
  paid: number,
): PaymentStatus {
  if (paid === 0) return 'not_paid';
  if (forecasted === 0) return paid === 0 ? 'matched' : 'overpaid';

  const variancePercent = ((paid - forecasted) / forecasted) * 100;

  // Check if matched (within 1%)
  if (Math.abs(variancePercent) < 1) return 'matched';

  // Underpaid (paid < forecasted)
  if (variancePercent < 0) {
    return Math.abs(variancePercent) < 5 ? 'slightly_underpaid' : 'underpaid';
  }

  // Overpaid (paid > forecasted)
  return variancePercent < 5 ? 'slightly_overpaid' : 'overpaid';
}

function getStatusConfig(status: PaymentStatus) {
  switch (status) {
    case 'matched':
      return {
        label: 'Matched',
        color: 'bg-green-100 text-green-800',
        icon: CheckCircleIcon,
      };
    case 'slightly_underpaid':
      return {
        label: 'Slightly Underpaid',
        color: 'bg-yellow-100 text-yellow-800',
        icon: WarningCircleIcon,
      };
    case 'underpaid':
      return {
        label: 'Underpaid',
        color: 'bg-red-100 text-red-800',
        icon: WarningCircleIcon,
      };
    case 'slightly_overpaid':
      return {
        label: 'Slightly Overpaid',
        color: 'bg-blue-100 text-blue-800',
        icon: WarningCircleIcon,
      };
    case 'overpaid':
      return {
        label: 'Overpaid',
        color: 'bg-purple-100 text-purple-800',
        icon: WarningCircleIcon,
      };
    case 'not_paid':
      return {
        label: 'Not Paid',
        color: 'bg-gray-100 text-gray-800',
        icon: WarningCircleIcon,
      };
    case 'closed':
      return {
        label: 'Closed',
        color: 'bg-slate-100 text-slate-700',
        icon: CheckCircleIcon,
      };
  }
}
const columns = [
  'Status',
  'Difference',
  'Rebate YTD',
  'Gross Amount',
  'Tax Withheld',
  'Net Paid',
  'Net Allocated',
  'Unallocated',
  'Type',
  'Memo',
];

const DebtorsReportTable: React.FC<DebtorsReportTableProps> = ({
  suppliers,
  paymentsData,
  rebateData,
  showClosedAgreements = false,
  dataSource = 'estimates',
}) => {
  // Owed YTD per supplier - sum all point-in-time rebate data to get cumulative
  const owedBySupplier = useMemo(() => {
    const map = new Map<string, number>();

    // Derive cumulative position from point-in-time rebate data
    const cumulativePosition = deriveCumulativePosition(rebateData || []);

    if (!cumulativePosition) return map;

    // Sum breakdowns by supplier to get YTD totals
    cumulativePosition.breakdowns.forEach((breakdown) => {
      const current = map.get(breakdown.supplierId) || 0;
      map.set(breakdown.supplierId, current + breakdown.rebateAmount);
    });

    return map;
  }, [rebateData]);

  // Group payments by supplier and compute paid totals
  const {
    paymentsBySupplier,
    paidBySupplier,
    allocatedBySupplier,
    supplierStatuses,
  } = useMemo(() => {
    const bySupplier = new Map<string, SupplierPayment[]>();
    const totals = new Map<string, number>();
    const allocatedTotals = new Map<string, number>();
    const statuses = new Map<string, PaymentStatus>();

    paymentsData?.forEach((p) => {
      const sid = p.rebateAgreement?.supplier?.id;
      if (!sid) return;
      if (!bySupplier.has(sid)) bySupplier.set(sid, []);
      const list = bySupplier.get(sid);
      if (list) list.push(p);
      totals.set(sid, (totals.get(sid) || 0) + p.amount);

      // Calculate allocated amount for this payment
      const allocatedAmount =
        p.allocatedAmount ??
        p.allocations?.reduce((sum, alloc) => sum + alloc.allocatedAmount, 0) ??
        0;
      allocatedTotals.set(
        sid,
        (allocatedTotals.get(sid) || 0) + allocatedAmount,
      );
    });

    // Calculate status for each supplier
    suppliers.forEach((supplier) => {
      // Check if supplier has any closed agreements
      const supplierPayments = bySupplier.get(supplier.id) || [];
      const hasClosedAgreement = supplierPayments.some(
        (p) => p.rebateAgreement?.isClosedOut,
      );

      if (hasClosedAgreement) {
        statuses.set(supplier.id, 'closed');
      } else {
        const forecasted = owedBySupplier.get(supplier.id) || 0;
        const paid = totals.get(supplier.id) || 0;
        statuses.set(supplier.id, calculatePaymentStatus(forecasted, paid));
      }
    });

    return {
      paymentsBySupplier: bySupplier,
      paidBySupplier: totals,
      allocatedBySupplier: allocatedTotals,
      supplierStatuses: statuses,
    };
  }, [paymentsData, suppliers, owedBySupplier]);

  // Transform data into flat BreakdownCell[] format
  const data = useMemo(() => {
    const cells: BreakdownCell[] = [];

    // Separate suppliers into active and closed, then sort each group
    // A supplier is "closed" only if ALL their payments have closed agreements
    // A supplier is "active" if ANY payment has an active (non-closed) agreement
    const activeSuppliers = suppliers.filter((supplier) => {
      const supplierPayments = paymentsBySupplier.get(supplier.id) || [];
      if (supplierPayments.length === 0) return true; // No payments = active
      // If ANY payment has an active agreement, supplier is active
      return supplierPayments.some((p) => !p.rebateAgreement?.isClosedOut);
    });

    const closedSuppliers = suppliers.filter((supplier) => {
      const supplierPayments = paymentsBySupplier.get(supplier.id) || [];
      if (supplierPayments.length === 0) return false; // No payments = not closed
      // Supplier is closed only if ALL payments have closed agreements
      return (
        supplierPayments.length > 0 &&
        supplierPayments.every((p) => p.rebateAgreement?.isClosedOut)
      );
    });

    // Sort each group
    const sortedActive = activeSuppliers.sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    const sortedClosed = closedSuppliers.sort((a, b) =>
      a.name.localeCompare(b.name),
    );

    // Combine: active first, then closed (if toggle is on)
    const sortedSuppliers = showClosedAgreements
      ? [...sortedActive, ...sortedClosed]
      : sortedActive;

    sortedSuppliers.forEach((supplier) => {
      const supplierPayments = paymentsBySupplier.get(supplier.id) || [];
      const isClosed = supplierPayments.some(
        (p) => p.rebateAgreement?.isClosedOut,
      );
      const forecasted = owedBySupplier.get(supplier.id) || 0;
      const paid = paidBySupplier.get(supplier.id) || 0;
      const allocated = allocatedBySupplier.get(supplier.id) || 0;
      const unallocated = paid - allocated;
      const difference = paid - forecasted;
      const status = supplierStatuses.get(supplier.id) || 'not_paid';

      const payments = (paymentsBySupplier.get(supplier.id) || []).sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      );

      // Calculate totals for gross and tax
      const totalGross = payments.reduce(
        (sum, p) => sum + (p.grossAmount || 0),
        0,
      );
      const totalTax = payments.reduce((sum, p) => sum + (p.taxAmount || 0), 0);

      // Supplier-level cells (subRow = null)
      columns.forEach((column) => {
        let value: number | string | null = null;

        if (column === 'Status') {
          value = status; // Store status as string, will be rendered specially
        } else if (column === 'Rebate YTD') {
          value = forecasted;
        } else if (column === 'Difference') {
          value = difference;
        } else if (column === 'Gross Amount') {
          value = totalGross > 0 ? totalGross : paid;
        } else if (column === 'Tax Withheld') {
          value = totalTax > 0 ? totalTax : null;
        } else if (column === 'Net Paid') {
          value = paid;
        } else if (column === 'Net Allocated') {
          value = allocated;
        } else if (column === 'Unallocated') {
          value = unallocated;
        } else if (column === 'Type' || column === 'Memo') {
          value = null; // Only shown for payment rows
        }

        cells.push({
          column,
          subColumn: null,
          row: supplier.name,
          subRow: null,
          value,
          metadata: {
            supplierId: supplier.id,
            isGroupLevel: true,
            status,
            forecasted, // Store in metadata so it's always available
            difference, // Store in metadata so it's always available
            isClosed, // Store closed status for styling
          },
        });
      });

      // Payment-level cells (subRow = payment date, subRowKey = payment ID for uniqueness)
      payments.forEach((payment) => {
        const paymentLabel = `Payment on ${formatPaymentDate(payment.date)}`;

        // Handle payment type - map enum to display name
        let paymentType: string | null = null;
        if (payment.type) {
          paymentType =
            REBATE_SUPPLIER_PAYMENT_TYPE_LABELS[
              payment.type as RebateSupplierPaymentType
            ] || payment.type;
        }

        // Handle memo - use if present and not empty
        let memoValue: string | null = null;
        if (payment.memo && payment.memo.trim() !== '') {
          memoValue = payment.memo.trim();
        }

        columns.forEach((column) => {
          let value: number | string | null = null;

          if (
            column === 'Status' ||
            column === 'Rebate YTD' ||
            column === 'Difference'
          ) {
            value = null; // Only shown at supplier level
          } else if (column === 'Gross Amount') {
            value = payment.grossAmount || payment.amount;
          } else if (column === 'Tax Withheld') {
            value = payment.taxAmount || null;
          } else if (column === 'Net Paid') {
            value = payment.amount;
          } else if (column === 'Net Allocated') {
            const paymentAllocated =
              payment.allocatedAmount ??
              payment.allocations?.reduce(
                (sum, alloc) => sum + alloc.allocatedAmount,
                0,
              ) ??
              0;
            value = paymentAllocated;
          } else if (column === 'Unallocated') {
            const paymentAllocated =
              payment.allocatedAmount ??
              payment.allocations?.reduce(
                (sum, alloc) => sum + alloc.allocatedAmount,
                0,
              ) ??
              0;
            value = payment.amount - paymentAllocated;
          } else if (column === 'Type') {
            value = paymentType;
          } else if (column === 'Memo') {
            value = memoValue;
          }

          cells.push({
            column,
            subColumn: null,
            row: supplier.name,
            subRow: paymentLabel,
            subRowKey: payment.id, // Use payment ID as unique key
            value,
            metadata: {
              supplierId: supplier.id,
              paymentId: payment.id,
              isGroupLevel: false,
              taxRate: payment.taxRate,
              isClosed, // Store closed status for styling
            },
          });
        });
      });
    });

    return cells;
  }, [
    suppliers,
    owedBySupplier,
    paidBySupplier,
    allocatedBySupplier,
    supplierStatuses,
    paymentsBySupplier,
    showClosedAgreements,
  ]);

  // Custom cell renderer
  const renderCell = (context: BreakdownCellContext) => {
    const { cell } = context;
    const metadata = cell.metadata as
      | {
          supplierId?: string;
          paymentId?: string;
          isGroupLevel?: boolean;
          status?: PaymentStatus;
          taxRate?: number | null;
          forecasted?: number;
          difference?: number;
          isClosed?: boolean;
        }
      | undefined;

    const column = cell.column;
    let value = cell.value;
    const isGroupLevel = metadata?.isGroupLevel ?? false;

    // For supplier-level rows, use metadata values for Forecasted and Difference
    // (these don't aggregate from payment cells, so we store them in metadata)
    if (isGroupLevel) {
      if (column === 'Rebate YTD' && metadata?.forecasted !== undefined) {
        value = metadata.forecasted;
      } else if (
        column === 'Difference' &&
        metadata?.difference !== undefined
      ) {
        value = metadata.difference;
      }
    }

    // Handle Status column with badge (only at supplier level)
    if (column === 'Status' && isGroupLevel) {
      const status = metadata?.status;
      const isClosed = metadata?.isClosed ?? false;
      const opacityClass = isClosed ? 'opacity-60' : '';
      if (status) {
        const config = getStatusConfig(status);
        const Icon = config.icon;
        return (
          <Badge
            className={`${config.color} text-xs cursor-default hover:bg-inherit ${opacityClass}`}
          >
            <Icon className="h-3 w-3 mr-1" />
            {config.label}
          </Badge>
        );
      }
      return (
        <span className={`text-xs text-muted-foreground ${opacityClass}`}>
          -
        </span>
      );
    }

    // Handle Status column for payment rows (should be null)
    if (column === 'Status' && !isGroupLevel) {
      return <span className="text-xs text-muted-foreground">-</span>;
    }

    // Apply grayed-out styling for closed agreements
    const isClosed = metadata?.isClosed ?? false;
    const opacityClass = isClosed ? 'opacity-60' : '';

    // Handle numeric values
    if (typeof value === 'number') {
      // For Difference column, use reconciliation colors
      if (column === 'Difference') {
        if (value === 0) {
          return (
            <span className={`text-xs text-muted-foreground ${opacityClass}`}>
              -
            </span>
          );
        }

        const colorClass =
          value > 0
            ? 'text-purple-600' // Overpaid
            : 'text-red-600'; // Underpaid

        return (
          <span className={`text-xs font-medium ${colorClass} ${opacityClass}`}>
            {value > 0 ? '+' : ''}
            {formatCurrency(value)}
          </span>
        );
      }

      // Color coding for Tax Withheld
      if (column === 'Tax Withheld') {
        const taxRate = metadata?.taxRate;
        // Handle -0 case by converting to positive 0
        const displayValue = value === 0 ? 0 : value;

        if (displayValue === 0) {
          return (
            <span className={`text-xs text-muted-foreground ${opacityClass}`}>
              -
            </span>
          );
        }

        return (
          <div className={`text-xs ${opacityClass}`}>
            <div className="font-medium text-red-600">
              -{formatCurrency(displayValue)}
            </div>
            {taxRate !== null && taxRate !== undefined && (
              <div className="text-[10px] text-gray-500">({taxRate}%)</div>
            )}
          </div>
        );
      }

      // Color coding for Net Paid
      if (column === 'Net Paid') {
        if (value === 0) {
          return (
            <span className={`text-xs text-muted-foreground ${opacityClass}`}>
              -
            </span>
          );
        }

        return (
          <span
            className={`text-xs font-medium text-green-700 ${opacityClass}`}
          >
            {formatCurrency(value)}
          </span>
        );
      }

      // Color coding for Net Allocated
      if (column === 'Net Allocated') {
        if (value === 0) {
          return (
            <span className={`text-xs text-muted-foreground ${opacityClass}`}>
              -
            </span>
          );
        }

        return (
          <span className={`text-xs font-medium text-blue-700 ${opacityClass}`}>
            {formatCurrency(value)}
          </span>
        );
      }

      // Color coding for Unallocated (highlight if > 0)
      if (column === 'Unallocated') {
        if (value === 0) {
          return (
            <span className={`text-xs text-muted-foreground ${opacityClass}`}>
              -
            </span>
          );
        }

        // Highlight unallocated amounts in orange/yellow to draw attention
        const colorClass =
          value > 0 ? 'text-orange-600' : 'text-muted-foreground';

        return (
          <span className={`text-xs font-medium ${colorClass} ${opacityClass}`}>
            {formatCurrency(value)}
          </span>
        );
      }

      // Color Rebate YTD based on data source
      if (column === 'Rebate YTD') {
        if (value === 0) {
          return (
            <span className={`text-xs text-muted-foreground ${opacityClass}`}>
              -
            </span>
          );
        }
        // Color based on data source: estimates=green, reported=black, predictions=blue
        const colorClass =
          dataSource === 'estimates'
            ? 'text-green-600'
            : dataSource === 'predictions'
              ? 'text-blue-600'
              : 'text-foreground';
        return (
          <span className={`text-xs font-medium ${colorClass} ${opacityClass}`}>
            {formatCurrency(value)}
          </span>
        );
      }

      // For other numeric columns (Gross Amount), use neutral color
      if (column === 'Gross Amount') {
        if (value === 0) {
          return (
            <span className={`text-xs text-muted-foreground ${opacityClass}`}>
              -
            </span>
          );
        }
      }

      return (
        <span className={`text-xs font-medium text-foreground ${opacityClass}`}>
          {formatCurrency(value)}
        </span>
      );
    }

    // Handle string values (Type, Memo)
    if (typeof value === 'string') {
      // Don't show empty strings or "0" as values
      if (value.trim() === '' || value === '0') {
        return (
          <span className={`text-xs text-muted-foreground ${opacityClass}`}>
            -
          </span>
        );
      }
      return (
        <span className={`text-xs font-medium text-foreground ${opacityClass}`}>
          {value}
        </span>
      );
    }

    // Handle null values
    return (
      <span className={`text-xs text-muted-foreground ${opacityClass}`}>-</span>
    );
  };

  // Custom aggregator for cell values (preserves strings, aggregates numbers)
  const aggregateCellValues = aggregateDebtorsReportCellValues;

  return (
    <BreakdownTable
      data={data}
      rowLabel="Supplier"
      subRowLabel="Payment Date"
      columnLabel="Metric"
      title="Debtors Report"
      renderCell={renderCell}
      aggregateCellValues={aggregateCellValues}
      showYDim1Total={true}
      maxHeight="calc(100vh - 200px)"
      xDimOrder={columns}
      hideVerticalLines={true}
      columnHeaderAlign="center"
      dataCellAlign="center"
    />
  );
};

export default DebtorsReportTable;
